"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";

interface CartSummaryBlockProps {
  total: number;
  viewCartHref?: string;
  checkoutHref?: string;
  checkoutLabel?: string;
  checkoutLoading?: boolean;
  onCheckoutClick?: () => void;
  className?: string;
  /** When true, checkout button is not full width (e.g. for sheet/sidebar) */
  compactButtons?: boolean;
}

export function CartSummaryBlock({
  total,
  viewCartHref,
  checkoutHref,
  checkoutLabel = "Checkout",
  checkoutLoading = false,
  onCheckoutClick,
  className,
  compactButtons = false,
}: CartSummaryBlockProps) {
  const router = useRouter();
  const [navigating, setNavigating] = useState(false);
  const loading = checkoutLoading || navigating;

  const handleCheckout = () => {
    if (onCheckoutClick) {
      onCheckoutClick();
      return;
    }
    if (checkoutHref) {
      setNavigating(true);
      router.push(checkoutHref);
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between w-full text-sm">
        <span className="font-medium">Total</span>
        <span className="font-semibold">$ {Number(total).toFixed(2)}</span>
      </div>
      <div className={`flex flex-col gap-2 mt-10 ${compactButtons ? "items-end" : ""}`}>
        {viewCartHref && (
          <Link href={viewCartHref} className="block cursor-pointer">
            <Button
              size="sm"
              variant="outline"
              className="w-full h-8 cursor-pointer"
            >
              View full cart
            </Button>
          </Link>
        )}
        {(checkoutHref || onCheckoutClick) && (
          <Button
            size="sm"
            disabled={loading}
            onClick={handleCheckout}
            className={
              compactButtons
                ? "min-w-[10rem] h-8 bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
                : viewCartHref
                  ? "w-full h-8 bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
                  : "cursor-pointer"
            }
          >
            {loading ? "Checkout…" : checkoutLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
